import type { DocSource } from "../types/chatTypes";
import { isSafeDocSourceUrl } from "./docSourceUrl";

// The SI Agent publishes its documentation citations in a dedicated channel rather than leaving them
// to the model's prose, and both response shapes carry the same `{ sources: DocSource[] }` payload:
//
//  - streaming (`stream: true`): a `data-sources` part. It is non-transient, so unlike
//    data-agent_start / data-error it lands in `message.parts` instead of only reaching the onData
//    callback, and it round-trips in the history on the next turn (the backend ignores it).
//  - non-streaming (`stream: false`): the `sources` field on the AgentResponse's text payload
//    (`response.data`).
//
// Both are read through the same validator below so the two paths can never disagree about which
// entries are renderable. (RTLCA-4937)
const SOURCES_PART_TYPE = "data-sources";

// Structural on purpose: this module stays free of the `ai` SDK types, so a UIMessage satisfies it
// and so does a plain object assembled by a caller or a test.
type PartsCarrier = { parts: readonly unknown[] };

function isDocSource(candidate: unknown): candidate is DocSource {
  if (typeof candidate !== "object" || candidate === null) return false;
  if (!("title" in candidate) || !("url" in candidate)) return false;
  const { title, url } = candidate;
  return typeof title === "string" && title.length > 0 && isSafeDocSourceUrl(url);
}

// The payload is `unknown` as far as the SDK (and axios) is concerned, so validate the shape here
// rather than trusting it — a malformed entry would otherwise render a chip with an empty label or
// a missing href. An absent `sources` key means the docs search tool wasn't used: no sources, not
// an error.
function readSourcesPayload(payload: unknown): DocSource[] {
  if (typeof payload !== "object" || payload === null || !("sources" in payload)) return [];
  const { sources } = payload;
  return Array.isArray(sources) ? sources.filter(isDocSource) : [];
}

// The backend already dedupes by page, but a streamed message can in principle carry more than one
// data-sources part (and the replayed history repeats earlier ones), so dedupe by url and keep the
// backend's relevance order.
function dedupeByUrl(sources: DocSource[]): DocSource[] {
  const seenUrls = new Set<string>();
  return sources.filter((source) => {
    if (seenUrls.has(source.url)) return false;
    seenUrls.add(source.url);
    return true;
  });
}

// Documentation pages cited by a streamed assistant message. The part is written once, after the
// last text delta, so this is empty until the message finalizes — text rendering never waits on it.
export function getMessageSources(message: PartsCarrier): DocSource[] {
  const sources: DocSource[] = [];

  for (const part of message.parts) {
    if (typeof part !== "object" || part === null) continue;
    if (!("type" in part) || part.type !== SOURCES_PART_TYPE || !("data" in part)) continue;
    sources.push(...readSourcesPayload(part.data));
  }

  return dedupeByUrl(sources);
}

// Documentation pages cited by a non-streaming agent reply (`stream: false`), where the whole answer
// arrives as one AgentResponse JSON and the citations sit on its text payload as
// `response.data.sources`. Feeds the same ChatSources chips as the streaming path.
export function getAgentResponseSources(response: unknown): DocSource[] {
  if (typeof response !== "object" || response === null || !("data" in response)) return [];
  return dedupeByUrl(readSourcesPayload(response.data));
}
